import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Dialog
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { driverTripUpdateAction, getDriverDashboard } from './driverDashboardSlice';
import { CheckCircleIcon, CheckIcon } from 'lucide-react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { keyframes } from '@emotion/react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { getMyProfileAction } from '../../../../../common/redux/reducer/commonSlices/myProfileSlice';
import {
  capitalizeFirstLetter,
  getCurrentDate,
  getTodayEpoch,
  useAbort
} from '../../../../../utils/commonFunctions';
import { Icon } from '@iconify/react';
import { addLogout } from '../../../../../common/redux/reducer/commonSlices/loginSlice';
import { deleteToken, getMessaging } from 'firebase/messaging';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../../../../common/components/buttons/CustomButton';

export default function DriverTripManager() {
  const data = useAppSelector(state => state.driverTripData?.data);
  const load = useAppSelector(state => state.UpdateGuest?.isLoading);
  const profile = useAppSelector(state => state.myProfile);
  const { isLoading: logoutLoader } = useAppSelector(state => state.logout);

  const [trips, setTrips] = useState([]);
  const [arrivedGuestID, setArrived] = useState<any>([]);

  const [noShow, setNoShow] = useState<any>([]);
  const [selectedTrip, setSelectedTrip] = useState<any>(null);

  const [selectedHotel, setSelectedHotel] = useState<any>(null);

  const [currentView, setCurrentView] = useState('tours');
  const [searchQuery, setSearchQuery] = useState('');
  const [hotelPicked, setHotelPicked] = useState(0);
  const [refreshLoader, setRefreshLoader] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [disable, setIsDisable] = useState(false);
  const [disableID, setIsDisableID] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [driverProfile, setDriverProfile] = useState<any>('');
  const [phoneNo, setPhoneNo] = useState<string>('');
  const [isLogout, setIsLogout] = useState(false);
  const [showSkipSuccess, setShowSkipSuccess] = useState(false);
  const [skippedTripName, setSkippedTripName] = useState('');
  const [isCall, setIsCall] = useState(false);
  const [isSkippingTrip, setIsSkippingTrip] = useState(false);
  const [isSkipDialogOpen, setIsSkipDialogOpen] = useState(false);
  const [tripToSkip, setTripToSkip] = useState<any>(null);
  const [hotelCount, setHotelCount] = useState(0);
  const [loadingTrips, setLoadingTrips] = useState<number | string | null>(null);

  const dispatch = useAppDispatch();
  const createAbort = useAbort();
  const navigate = useNavigate();
  const messaging = getMessaging();

  const handlePassengerArrival = (guestId: any, arrived: any) => {
    if (selectedHotel) {
      setSelectedHotel((prev: any) => ({
        ...prev,
        guests: prev.guests?.map((guest: any) =>
          guest.id === guestId ? { ...guest, arrived } : guest
        )
      }));
    } else if (selectedTrip) {
      setSelectedTrip((prev: any) => ({
        ...prev,
        guests: prev.guests?.map((guest: any) =>
          guest.id === guestId ? { ...guest, arrived } : guest
        )
      }));
    }

    if (arrived) {
      if (!arrivedGuestID.includes(guestId)) {
        arrivedGuestID.push(guestId);
      }
      if (noShow.includes(guestId)) {
        let updatedNoShow = noShow.filter((id: any) => id !== guestId);
        setNoShow(updatedNoShow);
      }
    } else {
      if (!noShow.includes(guestId)) {
        noShow.push(guestId);
      }
      setArrived((prev: any) => prev.filter((id: any) => id !== guestId));
    }

    setHasUnsavedChanges(true);
  };

  const handleTripStatus = async (trip: any) => {
    const tripId = trip.id;
    setLoadingTrips(tripId);

    const guestIds = trip.hotels?.flatMap((hotel: any) =>
      hotel.guests.map((guest: any) => guest.id)
    );

    const PVTGRPguestID = trip?.guests?.flatMap((guest: any) => guest.id);

    const payload = {
      guestIdsForTripStatus: guestIds ? guestIds : PVTGRPguestID,

      tripStatus: handleStatus(trip) === 'Completed' ? 'Completed' : 'Intransit',
      date: getTodayEpoch(driverProfile.timezone)
    };
    const action = await dispatch(driverTripUpdateAction(payload));

    const payloads = {
      driverID: driverProfile.driverid,
      autoplannerID: getTodayEpoch(driverProfile.timezone)
    };

    if (action.type === driverTripUpdateAction.fulfilled.type) {
      dispatch(getDriverDashboard(payloads));
      setLoadingTrips(tripId);
      if (handleStatus(trip) === 'Start Trip') {
        if (isPVTorGRP(trip.tourName)) {
          setCurrentView('guests');
        } else {
          setCurrentView('hotels');
        }
      }
    }
  };

  const handleSkipTrip = async (trip: any) => {
    const tripId = trip.id;
    setIsSkippingTrip(true);
    setLoadingTrips(null);

    const guestIds = trip.hotels?.flatMap((hotel: any) =>
      hotel.guests.map((guest: any) => guest.id)
    );

    const PVTGRPguestID = trip?.guests?.flatMap((guest: any) => guest.id);

    const payload = {
      skippedTripIds: guestIds ? guestIds : PVTGRPguestID,
      guestIdsForTripStatus: guestIds ? guestIds : PVTGRPguestID,

      tripStatus: 'Skipped',
      date: getTodayEpoch(driverProfile.timezone)
    };
    const action = await dispatch(driverTripUpdateAction(payload));

    const payloads = {
      driverID: driverProfile.driverid,
      autoplannerID: getTodayEpoch(driverProfile.timezone)
    };

    if (action.type === driverTripUpdateAction.fulfilled.type) {
      await dispatch(getDriverDashboard(payloads));
      setIsSkippingTrip(false);
      setLoadingTrips(null);
      setIsSkipDialogOpen(false);
      setSkippedTripName(trip.tourName);
      setShowSkipSuccess(true);

      setTimeout(() => {
        setShowSkipSuccess(false);
        setSkippedTripName('');
      }, 3000);
    }
  };

  const skipSuccessAnimation = keyframes`
  0% { 
    transform: scale(0.3) rotate(-45deg); 
    opacity: 0; 
  }
  50% { 
    transform: scale(1.1) rotate(0deg); 
    opacity: 1; 
  }
  100% { 
    transform: scale(1) rotate(0deg); 
    opacity: 1; 
  }
`;

  const bounceIn = keyframes`
  0% { 
    transform: scale(0.3); 
    opacity: 0; 
  }
  50% { 
    transform: scale(1.05); 
  }
  70% { 
    transform: scale(0.9); 
  }
  100% { 
    transform: scale(1); 
    opacity: 1; 
  }
`;

  const slideInUp = keyframes`
  0% { 
    transform: translateY(30px); 
    opacity: 0; 
  }
  100% { 
    transform: translateY(0); 
    opacity: 1; 
  }
`;

  const handleSave = async () => {
    setIsSaving(true);
    setLoadingTrips('');
    setTrips((prevTrips: any) =>
      prevTrips?.map((trip: any) => {
        if (trip.id === selectedTrip?.id) {
          if (selectedHotel) {
            return {
              ...trip,
              hotels: trip.hotels?.map((hotel: any) =>
                hotel.id === selectedHotel.id ? selectedHotel : hotel
              )
            };
          } else {
            return selectedTrip;
          }
        }
        return trip;
      })
    );

    const payload = {
      ...(noShow.length > 0 && { notArrivedGuestIds: noShow }),
      ...(arrivedGuestID.length > 0 && { arrivedGuestIds: arrivedGuestID }),

      date: getTodayEpoch(driverProfile.timezone)
    };
    const action = await dispatch(driverTripUpdateAction(payload));

    const payloads = {
      driverID: driverProfile.driverid,
      autoplannerID: getTodayEpoch(driverProfile.timezone)
    };

    if (action.type === driverTripUpdateAction.fulfilled.type) {
      dispatch(getDriverDashboard(payloads));
      setHasUnsavedChanges(false);
    }
    setIsSaving(false);
    setArrived([]);
    setNoShow([]);
  };

  const handleOpenCallOption = (phoneNumber: string) => {
    setPhoneNo(phoneNumber);
    setIsCall(true);
  };

  const handleSkipButtonClick = (event: any, trip: any) => {
    event?.stopPropagation();
    setTripToSkip(trip);
    setLoadingTrips(null);
    setIsSkipDialogOpen(true);
  };

  const handleCall = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWpCall = (phoneNumber: string) => {
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    window.open(`https://wa.me/${formattedNumber}`, '_blank');
  };

  const getArrivedCount = (guests: any[]) =>
    guests?.filter(guest => guest?.arrived === true || guest?.arrived === 'Arrived')
      .length;

  const filteredGuests = (guests: any[]) => {
    if (!searchQuery) return guests;
    return guests?.filter(
      guest =>
        guest.name.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        guest.contact?.includes(searchQuery)
    );
  };

  const isPVTorGRP = (tourName: string) => {
    return (
      tourName.toLowerCase().includes('pvt') ||
      tourName.toLowerCase().includes('grp') ||
      tourName.toLowerCase().includes('transfer') ||
      tourName.toLowerCase().includes('rlr')
    );
  };

  const handleBackNavigation = () => {
    if (selectedHotel) {
      setSelectedHotel(null);
      setCurrentView('hotels');
    } else if (currentView === 'hotels' || currentView === 'guests') {
      setCurrentView('tours');
    }
  };

  const getHeaderTitle = () => {
    if (selectedHotel) {
      return `🏨 ${selectedHotel.name}`;
    } else if (currentView === 'hotels' && selectedTrip) {
      return `🏨 ${selectedTrip.tourName}`;
    } else if (currentView === 'guests' && selectedTrip) {
      return `👥 ${selectedTrip.tourName} - Guests`;
    } else {
      return 'Dashboard';
    }
  };

  const showBackButton = () => {
    return currentView !== 'tours';
  };

  const handleManualRefresh = () => {
    setRefreshLoader(true);
    if (driverProfile.timezone) {
      const payload = {
        driverID: driverProfile.driverid,
        autoplannerID: getTodayEpoch(driverProfile.timezone)
      };
      setIsLoading(true);
      if (!profile.isLoading)
        dispatch(getDriverDashboard(payload)).finally(() => {
          setIsLoading(false);
          setRefreshLoader(false);
        });
    }
  };

  const handleTripCompleted = (trip: any) => {
    let allGuests: any[] = [];

    if (trip.hotels && trip.hotels.length > 0) {
      allGuests = trip.hotels.flatMap((hotel: any) => hotel.guests || []);
    } else if (trip.guests && trip.guests.length > 0) {
      allGuests = trip.guests;
    }

    const totalGuests = allGuests.length;
    const arrivedGuests = allGuests.filter((guest: any) => guest.arrived !== null).length;

    if (trip.status === 'Completed') {
      return 'complete';
    } else if (trip.status === 'Skipped') {
      return 'skipped';
    } else if (trip.status === 'Intransit' && arrivedGuests == 0) {
      return 'started';
    } else {
      return 'intransit';
    }
  };

  const handleStatus = (trip: any): string => {
    let allGuests: any[] = [];

    if (trip.hotels && trip.hotels.length > 0) {
      allGuests = trip.hotels.flatMap((hotel: any) => hotel.guests || []);
    } else if (trip.guests && trip.guests.length > 0) {
      allGuests = trip.guests;
    }

    const totalGuests = allGuests.length;
    const arrivedGuests = allGuests.filter((guest: any) => guest.arrived !== null).length;

    if (trip.status === 'Skipped') {
      return 'Skipped';
    } else if (arrivedGuests === 0) {
      return 'Start Trip';
    } else if (arrivedGuests < totalGuests) {
      return 'Intransit';
    } else {
      return 'Completed';
    }
  };

  const handleLogout = async () => {
    const fireBasetoken = localStorage.getItem('firebaseToken');
    await dispatch(addLogout({ fireBaseToken: fireBasetoken }));
    const key = localStorage.getItem('isKeepSignedIn');
    if (key === 'false') {
      localStorage.setItem('isKeepSignedIn', 'false');
      localStorage.clear();
    }
    localStorage.removeItem('lastVisitedPage');
    localStorage.removeItem('token');
    await deleteToken(messaging);
    navigate('/');
    dispatch({ type: 'RESET' });
    setIsLogout(false);
  };

  useEffect(() => {
    setTrips(data);
    if (selectedTrip) {
      const [hotel] = data.filter((hotel: any) => hotel?.id === selectedTrip.id);
      setSelectedTrip(hotel);
    }
  }, [data]);

  useEffect(() => {
    if (driverProfile.timezone) {
      const payload = {
        driverID: driverProfile.driverid,
        autoplannerID: getTodayEpoch(driverProfile.timezone)
      };
      if (!profile.isLoading)
        dispatch(getDriverDashboard(payload)).finally(() => {
          setIsLoading(false);
        });
    }
  }, [driverProfile]);

  // useEffect(() => {
  //   setIsLoading(true);
  //   dispatch(getMyProfileAction(createAbort().abortCall.signal));
  // }, []);

  useEffect(() => {
    if (selectedTrip) {
      let pickedHotelsCount = 0;

      const totalHotelCount = selectedTrip?.hotels?.length;

      setHotelCount(totalHotelCount);

      selectedTrip.hotels?.forEach((hotel: any) => {
        const hotelsCount = hotel.length;
        const totalGuests = hotel.guests?.length || 0;
        const arrivedGuests = hotel.guests?.filter(
          (guest: any) => guest.arrived != null
        ).length;

        if (arrivedGuests === totalGuests && totalGuests > 0) {
          pickedHotelsCount += 1;
        }
      });

      setHotelPicked(pickedHotelsCount);
    }
  }, [selectedTrip]);

  useEffect(() => {
    if (hotelPicked == selectedTrip?.hotels?.length) {
    }
  }, [hotelPicked]);

  useEffect(() => {
    const guestIds = data?.flatMap((trip: any) =>
      trip.hotels?.flatMap((hotel: any) => hotel.guests.map((guest: any) => guest.id))
    );
    const guestIntransist = data?.flatMap((trip: any) =>
      trip.hotels?.flatMap((hotel: any) =>
        hotel.guests.map((guest: any) => {
          if (guest.arrived !== null) {
          }
        })
      )
    );
  }, []);

  useEffect(() => {
    if (selectedTrip) {
      if (
        selectedTrip.status === 'Completed' ||
        selectedTrip.status === 'Upcoming' ||
        selectedTrip.status === 'Skipped'
      ) {
        setIsDisable(true);
        setIsDisableID(selectedTrip.id);
      } else {
        setIsDisable(false);
        setIsDisableID('');
      }
    }
  }, [selectedTrip]);

  useEffect(() => {
    if (profile.data) {
      setDriverProfile(profile.data);
    }
  }, [profile]);

  const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

  useEffect(() => {
    const preventZoom = (e: KeyboardEvent | WheelEvent) => {
      if ((e as WheelEvent).ctrlKey) {
        e.preventDefault();
      }

      if ((e as KeyboardEvent).ctrlKey) {
        const key = (e as KeyboardEvent).key;
        if (key === '+' || key === '-' || key === '=' || key === '0') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', preventZoom, { passive: false });
    document.addEventListener('keydown', preventZoom);

    return () => {
      document.removeEventListener('wheel', preventZoom);
      document.removeEventListener('keydown', preventZoom);
    };
  }, []);

  const hasIntransitTrip = (trips: any) => {
    return trips?.some((trip: any) => trip.status === 'Intransit');
  };

  const isFirstAvailableTrip = (trips: any, currentTrip: any) => {
    if (!trips || trips.length === 0) return false;

    const firstAvailableTrip = trips.find(
      (trip: any) => trip.status !== 'Intransit' && trip.status !== 'Completed'
    );

    return firstAvailableTrip?.id === currentTrip.id;
  };

  const shouldDisableStartButton = (trips: any, currentTrip: any) => {
    if (currentTrip.status === 'Intransit' || currentTrip.status === 'Completed') {
      return false;
    }

    if (currentTrip.status === 'Skipped') {
      return true;
    }

    const hasIntransit = trips.some((trip: any) => trip.status === 'Intransit');

    if (hasIntransit) {
      return true;
    }

    const currentTripIndex = trips.findIndex((trip: any) => trip.id === currentTrip.id);

    for (let i = 0; i < currentTripIndex; i++) {
      const previousTrip = trips[i];
      if (previousTrip.status !== 'Completed' && previousTrip.status !== 'Skipped') {
        return true;
      }
    }

    return false;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        height: '100vh'
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: { xs: '0.5rem', sm: '1rem' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {showBackButton() && (
              <Box
                sx={{
                  cursor: 'pointer',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '0.5rem',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)'
                  }
                }}
                onClick={handleBackNavigation}
              >
                <Icon icon='bx:arrow-back' width='24' height='24' />
              </Box>
            )}
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.0rem', sm: '1.0rem' },
                  fontWeight: 'bold',
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {getHeaderTitle() === 'Driver Dashboard' && (
                  <Icon icon='healthicons:truck-driver' width='24' height='24' />
                )}
                {getHeaderTitle()}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', opacity: 0.9 }}>
                {driverProfile
                  ? capitalizeFirstLetter(driverProfile.userName)
                  : 'Loading...'}{' '}
                {driverProfile.mappedVehicle
                  ? `• ${driverProfile.mappedVehicle.toUpperCase()}`
                  : ''}
              </Typography>
            </Box>
          </Box>
          {currentView === 'tours' && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '0.5rem',
                  p: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px'
                }}
                onClick={handleManualRefresh}
              >
                {isLoading && refreshLoader ? (
                  <Icon icon='line-md:loading-twotone-loop' width='30' height='30' />
                ) : (
                  <Icon icon='bx:refresh' width='30' height='30' />
                )}
              </Box>
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '0.5rem',
                  p: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setIsLogout(true)}
              >
                <Icon icon='ant-design:logout-outlined' width='30' height='30' />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          paddingBottom: hasUnsavedChanges ? '100px' : '20px'
        }}
      >
        {currentView === 'tours' && (
          <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: '1200px', margin: '0 auto' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Icon
                  icon='lets-icons:date-today-duotone'
                  width='30'
                  height='30'
                  style={{ color: 'blue', marginRight: '10px' }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.8rem' },
                    fontWeight: 'bold',
                    color: '#2d3748'
                  }}
                >
                  Today's Tours
                </Typography>
              </Box>
              <Typography
                variant='body1'
                sx={{
                  opacity: 0.9,
                  fontWeight: 300,
                  fontSize: '0.9rem'
                }}
              >
                {getCurrentDate(driverProfile.timezone)}
              </Typography>
            </Box>

            {isLoading && (
              <Box
                sx={{
                  height: '50vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',

                  animation: `${fadeIn} 0.6s ease-in-out`
                }}
              >
                <CircularProgress
                  size={70}
                  thickness={5}
                  sx={{ color: '#black', mb: 3 }}
                />
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 500,
                    letterSpacing: 1,
                    fontSize: '1.3rem',
                    textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }}
                >
                  🔄 Loading trips...
                </Typography>
              </Box>
            )}

            {!isLoading && (!trips || trips.length === 0) && (
              <Box
                sx={{
                  height: '50vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'fadeSlideIn 0.6s ease-in-out',
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: '1.3rem',
                    color: '#4a5568',
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  🚫 No Trips Assigned Today
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    color: '#718096'
                  }}
                >
                  Please check back later or contact your coordinator.
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fit, minmax(280px, 1fr))',
                  md: 'repeat(auto-fit, minmax(320px, 1fr))'
                },
                gap: { xs: 1, sm: 1.5, md: 2 },
                px: { xs: 1, sm: 2 },
                py: { xs: 1, sm: 2 }
              }}
            >
              {trips?.map((trip: any) => (
                <Box
                  key={trip.id}
                  sx={{
                    background: 'white',
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    borderRadius: { xs: '0.75rem', sm: '1rem' },
                    boxShadow: {
                      xs: '0 2px 12px rgba(0,0,0,0.06)',
                      sm: '0 4px 20px rgba(0,0,0,0.08)'
                    },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e2e8f0',
                    minHeight: { xs: 'auto', sm: '200px' },
                    '&:hover': {
                      transform: { xs: 'none', sm: 'translateY(-2px)' },
                      boxShadow: {
                        xs: '0 4px 16px rgba(102, 126, 234, 0.12)',
                        sm: '0 8px 30px rgba(102, 126, 234, 0.15)'
                      }
                    },
                    '&:active': {
                      transform: { xs: 'scale(0.98)', sm: 'translateY(-2px)' },
                      transition: 'transform 0.1s ease'
                    }
                  }}
                  onClick={() => {
                    setSelectedTrip(trip);
                    if (isPVTorGRP(trip.tourName)) {
                      setCurrentView('guests');
                    } else {
                      setCurrentView('hotels');
                    }
                  }}
                >
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: { xs: 0.5, sm: 1 },
                        mb: { xs: 1, sm: 1.5 },
                        flexDirection: { xs: 'column', sm: 'row' }
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#667eea',
                          fontSize: {
                            xs:
                              trip.tourName === 'PVT' ||
                              trip.tourName === 'GRP' ||
                              trip.tourName === 'TRANSFER' ||
                              trip.tourName === 'RLR'
                                ? '0.75rem'
                                : '0.9rem',
                            sm:
                              trip.tourName === 'PVT' ||
                              trip.tourName === 'GRP' ||
                              trip.tourName === 'TRANSFER' ||
                              trip.tourName === 'RLR'
                                ? '0.8rem'
                                : '1rem'
                          },
                          fontWeight: 'bold',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                          width: { xs: '100%', sm: '82%' },
                          mb: { xs: 0.5, sm: 0 }
                        }}
                      >
                        {trip.tourName === 'PVT' ||
                        trip.tourName === 'GRP' ||
                        trip.tourName === 'TRANSFER' ||
                        trip.tourName === 'RLR'
                          ? `${trip.fromLocation} ➜ ${trip.toLocation}`
                          : trip.tourName}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#667eea',
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          fontWeight: 'bold',
                          alignSelf: { xs: 'flex-start', sm: 'flex-start' }
                        }}
                      >
                        {`[${trip.mode}]`}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: { xs: 1.5, sm: 1 },
                        flexWrap: 'wrap'
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 0.4, sm: 0.5 },
                          borderRadius: '1rem',
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        🕐 {trip.timeFrom}
                      </Box>
                    </Box>

                    {loadingTrips !== 'Driver' && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexDirection: 'row',
                          gap: { xs: 1, sm: 1 },
                          flexWrap: { xs: 'wrap', sm: 'nowrap' }
                        }}
                      >
                        <Typography
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#718096',
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            flex: '0 0 auto'
                          }}
                        >
                          👥 {trip.totalGuests} Guests
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: { xs: 0.5, sm: 1 },
                            alignItems: 'center',
                            flex: '0 0 auto'
                          }}
                        >
                          {loadingTrips !== trip.id &&
                            handleStatus(trip) === 'Start Trip' &&
                            trip.status !== 'Intransit' &&
                            trip.status !== 'Skipped' && (
                              <Button
                                variant='outlined'
                                disabled={
                                  shouldDisableStartButton(trips, trip) || isSkippingTrip
                                }
                                onClick={event => handleSkipButtonClick(event, trip)}
                                sx={{
                                  background:
                                    'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                  color: '#6c757d',
                                  border: '2px solid #dee2e6',
                                  px: { xs: 1.5, sm: 2 },
                                  py: { xs: 0.8, sm: 1 },
                                  borderRadius: '1rem',
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  transition: 'all 0.3s ease',
                                  width: { xs: 'auto', sm: 'auto' },
                                  minWidth: { xs: '80px', sm: '100px' },
                                  boxShadow: '0 2px 8px rgba(108, 117, 125, 0.2)',
                                  position: 'relative',
                                  padding: '8px !important',
                                  overflow: 'hidden',
                                  opacity: isSkippingTrip ? 0.7 : 1,
                                  '&:hover': {
                                    background: isSkippingTrip
                                      ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                                      : 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                                    borderColor: isSkippingTrip ? '#dee2e6' : '#adb5bd',
                                    color: isSkippingTrip ? '#6c757d' : '#495057',
                                    transform: {
                                      xs: 'none',
                                      sm: isSkippingTrip ? 'none' : 'translateY(-1px)'
                                    },
                                    boxShadow: isSkippingTrip
                                      ? '0 2px 8px rgba(108, 117, 125, 0.2)'
                                      : '0 4px 12px rgba(108, 117, 125, 0.3)'
                                  },
                                  '&:active': {
                                    transform: { xs: 'scale(0.98)', sm: 'translateY(0)' },
                                    boxShadow: '0 2px 4px rgba(108, 117, 125, 0.2)'
                                  }
                                }}
                              >
                                ⏭️ Skip
                              </Button>
                            )}

                          {handleTripCompleted(trip) === 'complete' ||
                          handleTripCompleted(trip) === 'started' ||
                          handleTripCompleted(trip) === 'skipped' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                gap: 1,
                                color:
                                  handleTripCompleted(trip) === 'skipped'
                                    ? '#f59e0b'
                                    : '#28a745',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: 600,
                                opacity: 0.8,
                                p: { xs: 1, sm: 0 }
                              }}
                            >
                              {handleTripCompleted(trip) === 'complete' ? (
                                <CheckCircleIcon />
                              ) : handleTripCompleted(trip) === 'skipped' ? (
                                <SkipNextIcon
                                  sx={{
                                    fontSize: { xs: '1rem', sm: '1.2rem' },
                                    color: '#f59e0b',
                                    animation: 'skipPulse 2s infinite',
                                    '@keyframes skipPulse': {
                                      '0%, 100%': {
                                        transform: 'scale(1)',
                                        opacity: 1
                                      },
                                      '50%': {
                                        transform: 'scale(1.1)',
                                        opacity: 0.7
                                      }
                                    }
                                  }}
                                />
                              ) : (
                                <DirectionsCarIcon
                                  sx={{
                                    fontSize: { xs: '1rem', sm: '1.2rem' },
                                    color: '#28a745',
                                    animation: 'bounce 1.5s infinite',
                                    '@keyframes bounce': {
                                      '0%, 20%, 50%, 80%, 100%': {
                                        transform: 'translateY(0)'
                                      },
                                      '40%': { transform: 'translateY(-4px)' },
                                      '60%': { transform: 'translateY(-2px)' }
                                    }
                                  }}
                                />
                              )}

                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  color:
                                    handleTripCompleted(trip) === 'skipped'
                                      ? '#f59e0b'
                                      : '#6c757d',
                                  textAlign: { xs: 'center', sm: 'left' }
                                }}
                              >
                                {handleTripCompleted(trip) === 'complete'
                                  ? 'Trip Completed'
                                  : handleTripCompleted(trip) === 'skipped'
                                  ? 'Trip Skipped'
                                  : 'Intransit'}
                              </Typography>
                            </Box>
                          ) : (handleStatus(trip) === 'Completed' ||
                              handleStatus(trip) === 'Start Trip') &&
                            handleTripCompleted(trip) !== 'complete' &&
                            handleTripCompleted(trip) !== 'started' &&
                            handleTripCompleted(trip) !== 'skipped' ? (
                            <Button
                              variant='contained'
                              disabled={shouldDisableStartButton(trips, trip)}
                              startIcon={
                                loadingTrips === trip.id ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : handleStatus(trip) === 'Start Trip' ? (
                                  <PlayArrowIcon />
                                ) : (
                                  <CheckIcon />
                                )
                              }
                              onClick={(event: any) => {
                                event.stopPropagation();
                                if (!shouldDisableStartButton(trips, trip)) {
                                  handleTripStatus(trip);
                                  setSelectedTrip(trip);
                                }
                              }}
                              sx={{
                                backgroundColor:
                                  handleStatus(trip) === 'Start Trip'
                                    ? '#007bff'
                                    : handleStatus(trip) === 'Intransit'
                                    ? '#6c757d'
                                    : '#198754',
                                '&:hover:not(:disabled)': {
                                  backgroundColor:
                                    handleStatus(trip) === 'Start Trip'
                                      ? '#0056b3'
                                      : handleStatus(trip) === 'Intransit'
                                      ? '#5a6268'
                                      : '#157347'
                                },
                                borderRadius: '8px',
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: '0.85rem',
                                minWidth: 100,
                                px: 2,
                                py: 1,
                                textTransform: 'none',
                                transition: '0.3s ease',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                              }}
                            >
                              {loadingTrips === trip.id
                                ? handleStatus(trip) === 'Start Trip'
                                  ? 'Starting...'
                                  : 'Completing...'
                                : shouldDisableStartButton(trips, trip) &&
                                  handleStatus(trip) === 'Start Trip'
                                ? 'Upcoming...'
                                : handleStatus(trip) === 'Start Trip'
                                ? 'Start Trip'
                                : 'Complete'}
                            </Button>
                          ) : handleStatus(trip) === 'Intransit' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                gap: 1,
                                color: '#ff6b35',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: 600,
                                position: 'relative',
                                p: { xs: 1, sm: 0 }
                              }}
                            >
                              <DirectionsCarIcon
                                sx={{
                                  fontSize: { xs: '1rem', sm: '1.2rem' },
                                  color: '#28a745',
                                  animation: 'bounce 1.5s infinite',
                                  '@keyframes bounce': {
                                    '0%, 20%, 50%, 80%, 100%': {
                                      transform: 'translateY(0)'
                                    },
                                    '40%': { transform: 'translateY(-4px)' },
                                    '60%': { transform: 'translateY(-2px)' }
                                  }
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  animation: 'fadeInOut 2s infinite',
                                  '@keyframes fadeInOut': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.6 }
                                  }
                                }}
                              >
                                {handleStatus(trip)}
                              </Typography>
                            </Box>
                          ) : handleStatus(trip) === 'Skipped' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                gap: 1,
                                color: '#f59e0b',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: 600,
                                position: 'relative',
                                p: { xs: 1, sm: 0 }
                              }}
                            >
                              <SkipNextIcon
                                sx={{
                                  fontSize: { xs: '1rem', sm: '1.2rem' },
                                  color: '#f59e0b',
                                  animation: 'skipGlow 3s infinite',
                                  '@keyframes skipGlow': {
                                    '0%, 100%': {
                                      filter:
                                        'drop-shadow(0 0 5px rgba(245, 158, 11, 0.5))',
                                      transform: 'scale(1)'
                                    },
                                    '50%': {
                                      filter:
                                        'drop-shadow(0 0 10px rgba(245, 158, 11, 0.8))',
                                      transform: 'scale(1.05)'
                                    }
                                  }
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  animation: 'skipTextGlow 3s infinite',
                                  '@keyframes skipTextGlow': {
                                    '0%, 100%': {
                                      textShadow: '0 0 5px rgba(245, 158, 11, 0.3)'
                                    },
                                    '50%': {
                                      textShadow: '0 0 10px rgba(245, 158, 11, 0.6)'
                                    }
                                  }
                                }}
                              >
                                {handleStatus(trip)}
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                gap: 1,
                                color: '#495057',
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                fontWeight: 600,
                                position: 'relative',
                                p: { xs: 1, sm: 0 }
                              }}
                            >
                              <AccessTimeIcon
                                sx={{
                                  fontSize: { xs: '1rem', sm: '1.2rem' },
                                  color: '#6c757d',
                                  animation: 'pulse 2s infinite',
                                  '@keyframes pulse': {
                                    '0%': { opacity: 1, transform: 'scale(1)' },
                                    '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                                    '100%': { opacity: 1, transform: 'scale(1)' }
                                  }
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                  animation: 'glow 3s infinite',
                                  '@keyframes glow': {
                                    '0%, 100%': {
                                      textShadow: '0 0 5px rgba(73, 80, 87, 0.3)'
                                    },
                                    '50%': {
                                      textShadow: '0 0 10px rgba(73, 80, 87, 0.6)'
                                    }
                                  }
                                }}
                              >
                                {handleStatus(trip)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {currentView === 'hotels' && selectedTrip && (
          <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: '1200px', margin: '0 auto' }}>
            <Box
              sx={{
                backgroundColor: '#f8fafc',
                p: 1,
                borderRadius: '1rem',
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #e2e8f0'
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2d3748' }}
                >
                  {hotelPicked}/{selectedTrip ? selectedTrip?.hotels?.length : 0}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#718096' }}>
                  Picked
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: `conic-gradient(#48bb78 ${
                    (hotelPicked / hotelCount) * 360
                  }deg, #e2e8f0 0deg)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}
                >
                  {Math.round((hotelPicked / hotelCount) * 100)}%
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fit, minmax(300px, 1fr))'
                },
                gap: { xs: 1.5, sm: 2 }
              }}
            >
              {selectedTrip.hotels?.map((hotel: any) => (
                <Box
                  key={hotel.id}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)'
                    }
                  }}
                  onClick={() => {
                    setSelectedHotel(hotel);
                    setCurrentView('guests');
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        mb: 2,
                        width: 'calc(100% - 100px)'
                      }}
                    >
                      🏨 {hotel.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        mb: 2,
                        background: 'rgba(255,255,255,0.05)',
                        p: 1,
                        borderRadius: '1rem'
                      }}
                    >
                      🕐 {hotel.guests[0].tripStartTime}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography sx={{ fontSize: '0.9rem' }}>
                      👥 {hotel.guests.length} Booking
                      {hotel.guests?.length > 1 && 's'}
                    </Typography>

                    <Box
                      sx={{
                        background: 'rgba(255,255,255,0.2)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '1rem',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}
                    >
                      {getArrivedCount(hotel.guests)}/{hotel.guests.length} Onboarded
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {currentView === 'guests' && (selectedTrip || selectedHotel) && (
          <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: '800px', margin: '0 auto' }}>
            <TextField
              fullWidth
              placeholder='Search guests by name or contact...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position='start'>🔍</InputAdornment>
              }}
            />
            <Box
              sx={{
                backgroundColor: '#f8fafc',
                p: 1,
                borderRadius: '1rem',
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #e2e8f0'
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2d3748' }}
                >
                  {getArrivedCount(
                    selectedHotel ? selectedHotel.guests : selectedTrip.guests
                  )}
                  /
                  {selectedHotel
                    ? selectedHotel.guests.length
                    : selectedTrip.guests.length}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#718096' }}>
                  {getArrivedCount(
                    selectedHotel ? selectedHotel.guests : selectedTrip.guests
                  ) > 1
                    ? 'Families'
                    : 'Family'}{' '}
                  {'Picked'}{' '}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: `conic-gradient(#48bb78 ${
                    (getArrivedCount(
                      selectedHotel ? selectedHotel.guests : selectedTrip.guests
                    ) /
                      (selectedHotel
                        ? selectedHotel.guests.length
                        : selectedTrip.guests.length)) *
                    360
                  }deg, #e2e8f0 0deg)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}
                >
                  {Math.round(
                    (getArrivedCount(
                      selectedHotel ? selectedHotel.guests : selectedTrip.guests
                    ) /
                      (selectedHotel
                        ? selectedHotel.guests.length
                        : selectedTrip.guests.length)) *
                      100
                  )}
                  %
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              {filteredGuests(selectedHotel ? selectedHotel.guests : selectedTrip.guests)
                .length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    backgroundColor: '#fff5f5',
                    borderRadius: '1rem',
                    border: '2px dashed #feb2b2',
                    color: '#c53030',
                    mt: 2
                  }}
                >
                  <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1 }}>
                    😕 No Guests Found
                  </Typography>
                  <Typography variant='body2'>
                    Try changing your search terms or check the guest list.
                  </Typography>
                </Box>
              ) : (
                filteredGuests(
                  selectedHotel ? selectedHotel.guests : selectedTrip.guests
                ).map((guest: any, index: any) => (
                  <Box key={guest.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        p: { xs: 1.5, sm: 2 },
                        gap: { xs: 1, sm: 1.5 },
                        backgroundColor: 'white',
                        borderRadius:
                          index === 0
                            ? '1rem 1rem 0 0'
                            : index ===
                              (selectedHotel
                                ? selectedHotel.guests.length
                                : selectedTrip.guests.length) -
                                1
                            ? '0 0 1rem 1rem'
                            : '0',
                        border: '1px solid #e2e8f0',
                        borderTop: index === 0 ? '1px solid #e2e8f0' : 'none'
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '40px', sm: '48px' },
                          height: { xs: '40px', sm: '48px' },
                          borderRadius: '50%',
                          backgroundColor: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                      >
                        {guest.name.charAt(0)}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: '600',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {guest.name}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            flexWrap: 'wrap'
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: '#e8f4fd',
                              px: 1,
                              py: 0.25,
                              borderRadius: '0.5rem',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              color: '#1976d2'
                            }}
                          >
                            👨‍👩‍👧‍👦 {guest.adults} Adult{guest.adults !== 1 ? 's' : ''}
                            {guest.children > 0 &&
                              `, ${guest.children} Child${
                                guest.children !== 1 ? 'ren' : ''
                              }`}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap'
                          }}
                        >
                          {guest.contact && (
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                color: '#667eea',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}
                              onClick={() => handleOpenCallOption(guest.contact)}
                            >
                              📞 {guest.contact}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.8rem', color: '#718096' }}>
                        {guest.arrived === 'Arrived' || guest.arrived === true ? (
                          <Icon
                            icon='teenyicons:tick-circle-outline'
                            width='24'
                            height='24'
                            style={{ color: '#48bb78' }}
                          />
                        ) : guest.arrived === 'Not Arrived' || guest.arrived === false ? (
                          <Icon
                            icon='healthicons:no-outline-24px'
                            width='24'
                            height='24'
                            style={{ color: '#f56565' }}
                          />
                        ) : (
                          <Icon
                            icon='tabler:clock'
                            width='24'
                            height='24'
                            style={{ color: 'orange' }}
                          />
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          gap: 0.5,
                          width: '100%'
                        }}
                      >
                        <Box
                          sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: 1,
                            borderRadius: '0.5rem',
                            width: '48%',
                            border: '1px solid #f56565',
                            cursor: disable ? 'not-allowed' : 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            textAlign: 'center',
                            backgroundColor:
                              guest.arrived === 'Not Arrived' || guest.arrived === false
                                ? '#f56565'
                                : 'transparent',
                            color:
                              guest.arrived === 'Not Arrived' || guest.arrived === false
                                ? 'white'
                                : '#f56565',
                            opacity: disable ? 0.5 : 1,
                            pointerEvents: disable ? 'none' : 'auto',
                            '&:hover': {
                              opacity: disable ? 0.5 : 0.8
                            }
                          }}
                          onClick={() =>
                            !disable && handlePassengerArrival(guest.id, false)
                          }
                        >
                          ❌ No Show
                        </Box>
                        <Box
                          sx={{
                            px: { xs: 1, sm: 1.5 },
                            py: 1,
                            borderRadius: '0.5rem',
                            border: '1px solid #48bb78',
                            width: '48%',
                            cursor: disable ? 'not-allowed' : 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            textAlign: 'center',
                            backgroundColor:
                              guest.arrived === 'Arrived' || guest.arrived === true
                                ? '#48bb78'
                                : 'transparent',
                            color:
                              guest.arrived === 'Arrived' || guest.arrived === true
                                ? 'white'
                                : '#48bb78',
                            opacity: disable ? 0.5 : 1,
                            pointerEvents: disable ? 'none' : 'auto',
                            '&:hover': {
                              opacity: disable ? 0.5 : 0.8
                            }
                          }}
                          onClick={() =>
                            !disable && handlePassengerArrival(guest.id, true)
                          }
                        >
                          ✅ Onboarded
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>

      {hasUnsavedChanges && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 20, sm: 30 },
            right: { xs: 20, sm: 30 },
            zIndex: 1000
          }}
        >
          <Box
            onClick={load ? undefined : handleSave}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              borderRadius: '2rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#fff',
              background: load
                ? 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)'
                : 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)',
              boxShadow: load
                ? '0 4px 15px rgba(113, 128, 150, 0.4)'
                : '0 6px 20px rgba(56, 161, 105, 0.4)',
              cursor: load ? 'not-allowed' : 'pointer',
              opacity: load ? 0.8 : 1,
              transition: 'all 0.3s ease',
              pointerEvents: load ? 'none' : 'auto',
              '&:hover': {
                transform: load ? 'none' : 'translateY(-2px)',
                boxShadow: load
                  ? '0 4px 15px rgba(113, 128, 150, 0.4)'
                  : '0 8px 24px rgba(56, 161, 105, 0.5)'
              }
            }}
          >
            {load ? (
              <>
                <CircularProgress size={18} sx={{ color: 'white' }} />
                Saving...
              </>
            ) : (
              <>💾 Save </>
            )}
          </Box>
        </Box>
      )}
      <Dialog open={isLogout} onClose={() => setIsLogout(false)}>
        <Box
          sx={{
            textAlign: 'center',
            padding: '5%',
            minWidth: '250px',
            maxWidth: '350px'
          }}
        >
          <Typography>Are you sure you want to logout?</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingTop: '5%',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ marginRight: '12px' }}>
              <CustomButton
                className='saveChanges'
                loading={logoutLoader}
                category='Yes'
                onClick={handleLogout}
              />
            </Box>
            <CustomButton
              className='cancel'
              category='No'
              onClick={() => {
                setIsLogout(false);
              }}
            />
          </Box>
        </Box>
      </Dialog>
      <Dialog open={isCall} onClose={() => setIsCall(false)}>
        <Box
          sx={{
            textAlign: 'center',
            padding: { xs: '16px', sm: '20px' },
            minWidth: { xs: '280px', sm: '250px' },
            maxWidth: { xs: '90vw', sm: '350px' },
            width: '100%'
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '18px', sm: '20px' },
              fontWeight: 500,
              mb: 2
            }}
          >
            Choose Contact Method
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 2, sm: 2.5 },
              mb: 2
            }}
          >
            <Box
              onClick={() => {
                handleWpCall(phoneNo);
              }}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#25D366',
                p: { xs: 1.2, sm: 1.5 },
                height: 'fit-content',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '48px',
                minHeight: '48px'
              }}
            >
              <Icon
                icon='ic:outline-whatsapp'
                width='1.5rem'
                height='1.5rem'
                style={{ color: '#fff' }}
              />
            </Box>
            <Box
              sx={{
                borderRadius: '12px',
                backgroundColor: '#34B7F1',
                p: { xs: 1.2, sm: 1.5 },
                cursor: 'pointer',
                height: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '48px',
                minHeight: '48px'
              }}
              onClick={() => {
                handleCall(phoneNo);
              }}
            >
              <Icon
                icon='proicons:call'
                width='1.5rem'
                height='1.5rem'
                style={{ color: '#fff' }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Typography
              sx={{
                color: 'red',
                cursor: 'pointer',
                fontSize: { xs: '14px', sm: '16px' },
                padding: { xs: '8px', sm: '12px' }
              }}
              onClick={() => {
                setIsCall(false);
              }}
            >
              Close
            </Typography>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={isSkipDialogOpen}
        onClose={() => !isSkippingTrip && setIsSkipDialogOpen(false)}
        disableEscapeKeyDown={isSkippingTrip}
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 3 },
            maxHeight: { xs: 'calc(100vh - 16px)', sm: 'calc(100vh - 64px)' },
            width: { xs: 'calc(100vw - 16px)', sm: 'auto' }
          }
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            padding: { xs: '20px', sm: '30px' },
            minWidth: { xs: 'auto', sm: '300px' },
            maxWidth: { xs: '100%', sm: '400px' },
            width: '100%'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Icon
              icon='material-symbols:warning-outline'
              width='48'
              height='48'
              style={{ color: '#f59e0b' }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: { xs: '18px', sm: '20px' },
              fontWeight: 600,
              mb: 2,
              color: '#2d3748'
            }}
          >
            Skip Trip Confirmation
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              color: '#718096',
              mb: 3,
              lineHeight: 1.5,
              wordBreak: 'break-word'
            }}
          >
            Are you sure you want to skip this trip?
            <br />
            <strong>{tripToSkip?.tourName}</strong>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 2 },
              justifyContent: 'center'
            }}
          >
            <Button
              variant='contained'
              disabled={isSkippingTrip}
              onClick={() => handleSkipTrip(tripToSkip)}
              startIcon={
                isSkippingTrip ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: '#fff',
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round'
                      }
                    }}
                  />
                ) : null
              }
              sx={{
                px: 3,
                py: 1,
                borderRadius: '0.5rem',
                fontSize: { xs: '13px', sm: '14px' },
                fontWeight: 500,
                textTransform: 'none',
                backgroundColor: isSkippingTrip ? '#9ca3af' : '#ef4444',
                minWidth: { xs: '100%', sm: '130px' },
                '&:hover': {
                  backgroundColor: isSkippingTrip ? '#9ca3af' : '#dc2626'
                },
                '&:disabled': {
                  backgroundColor: '#9ca3af',
                  color: '#fff'
                }
              }}
            >
              {isSkippingTrip ? 'Skipping...' : 'Yes'}
            </Button>
            <Button
              variant='outlined'
              disabled={isSkippingTrip}
              onClick={() => {
                setIsSkipDialogOpen(false);
                setTripToSkip(null);
              }}
              sx={{
                px: 3,
                py: 1,
                borderRadius: '0.5rem',
                fontSize: { xs: '13px', sm: '14px' },
                fontWeight: 500,
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#6b7280',
                opacity: isSkippingTrip ? 0.5 : 1,
                minWidth: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  borderColor: isSkippingTrip ? '#d1d5db' : '#9ca3af',
                  backgroundColor: isSkippingTrip ? 'transparent' : '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={showSkipSuccess}
        onClose={() => setShowSkipSuccess(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: '1rem', sm: '1.5rem' },
            padding: { xs: '16px', sm: '20px' },
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '2px solid #0ea5e9',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            animation: `${bounceIn} 0.6s ease-out`,
            margin: { xs: 1, sm: 3 },
            maxHeight: { xs: 'calc(100vh - 16px)', sm: 'calc(100vh - 64px)' },
            width: { xs: 'calc(100vw - 16px)', sm: 'auto' }
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            padding: { xs: '16px', sm: '20px' },
            minWidth: { xs: 'auto', sm: '320px' },
            maxWidth: { xs: '100%', sm: '400px' },
            width: '100%'
          }}
        >
          <Box
            sx={{
              mb: { xs: 2, sm: 3 },
              animation: `${skipSuccessAnimation} 0.8s ease-out`
            }}
          >
            <Box
              sx={{
                width: { xs: '64px', sm: '80px' },
                height: { xs: '64px', sm: '80px' },
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  animation: 'pulse 2s infinite',
                  zIndex: -1
                },
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.7
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1
                  }
                }
              }}
            >
              <Icon icon='material-symbols:skip-next-rounded' />
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: { xs: '20px', sm: '24px' },
              fontWeight: 700,
              mb: 2,
              color: '#0c4a6e',
              animation: `${slideInUp} 0.6s ease-out 0.2s both`
            }}
          >
            Trip Skipped Successfully!
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              color: '#0369a1',
              mb: 2,
              fontWeight: 500,
              animation: `${slideInUp} 0.6s ease-out 0.4s both`,
              wordBreak: 'break-word'
            }}
          >
            <strong>{skippedTripName}</strong> has been skipped
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '12px', sm: '14px' },
              color: '#075985',
              opacity: 0.8,
              animation: `${slideInUp} 0.6s ease-out 0.6s both`
            }}
          >
            You can now proceed with the next trip
          </Typography>

          <Box
            sx={{
              width: '100%',
              height: '4px',
              backgroundColor: '#e0f2fe',
              borderRadius: '2px',
              mt: { xs: 2, sm: 3 },
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, #0ea5e9, #0284c7)',
                borderRadius: '2px',
                animation: 'progressBar 3s linear',
                '@keyframes progressBar': {
                  '0%': {
                    transform: 'translateX(-100%)'
                  },
                  '100%': {
                    transform: 'translateX(0%)'
                  }
                }
              }}
            />
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
